import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { getAdminRecipes } from '../../actions/adminRecipes';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

const SearchBar = ({
  queryInput,
  parsedQuery,
  onInputChange,
  onSubmit,
  onReset,
  onNewRecipe,
}: {
  queryInput: string;
  parsedQuery: string;
  onInputChange: (e: any) => void;
  onSubmit: (e: any) => void;
  onReset: () => void;
  onNewRecipe: () => void;
}) => (
  <div className="admin-card-body" style={{ borderBottom: '1px solid #e1e3e5', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
    <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, flex: 1 }}>
      <input
        className="form-control form-control-sm"
        placeholder="Search recipes…"
        value={queryInput}
        onChange={onInputChange}
        style={{ maxWidth: 300, fontSize: '0.82rem' }}
      />
    </form>
    {parsedQuery && (
      <span
        className="status-badge cancelled"
        style={{ cursor: 'pointer', fontSize: '0.75rem' }}
        onClick={onReset}
      >
        {parsedQuery} &times;
      </span>
    )}
    <button
      className="admin-dropdown-trigger"
      onClick={onNewRecipe}
      type="button"
      style={{ background: '#ec174f', color: '#fff', borderColor: '#ec174f' }}
    >
      + New Recipe
    </button>
  </div>
);

const RecipesTable = ({ recipes, onRowClick }: { recipes: any[]; onRowClick: (r: any) => void }) => (
  <div className="table-responsive">
    <table className="admin-table">
      <thead>
        <tr>
          <th>Image</th>
          <th>Title</th>
          <th>Calories</th>
          <th>Total Time</th>
          <th>Servings</th>
          <th>Last Updated</th>
        </tr>
      </thead>
      <tbody>
        {recipes.map((item: any) => (
          <tr key={item._id} onClick={() => onRowClick(item)}>
            <td style={{ width: 60 }}>
              <img src={item.recipeImage} alt={item.title} style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />
            </td>
            <td style={{ fontWeight: 600 }}>{item.title}</td>
            <td>{item.caloriesPerServing}</td>
            <td>{item.totalTime}</td>
            <td>{item.servings}</td>
            <td className="order-time">{moment(item.lastUpdated).format('MMM Do YY')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pagination = ({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
}: {
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
}) => (
  <div className="admin-card-body" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
    <button
      className="admin-dropdown-trigger"
      onClick={onPrevious}
      disabled={currentPage <= 1}
      style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
    >
      Previous
    </button>
    <button
      className="admin-dropdown-trigger"
      onClick={onNext}
      disabled={currentPage >= totalPages}
      style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
    >
      Next
    </button>
  </div>
);

const RecipesDashBoard = (props: any) => {
  const history = useHistory();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState(location.search);
  const [queryInput, setQueryInput] = useState('');

  const searchQueryInitialized = useRef(false);
  const locationSearchInitialized = useRef(false);

  const updateSearchQuery = (parsed: any) => setSearchQuery('?' + queryString.stringify(parsed));

  useEffect(() => {
    const parsed = queryString.parse(location.search) as any;
    let page: any = 1, filterVal: any, query: any;

    if (parsed.page) page = parsed.page;
    if (parsed.filter) filterVal = parsed.filter;
    if (parsed.query) { query = parsed.query; setQueryInput(''); }

    props.getAdminRecipes(page, filterVal, query);
  }, []);

  useEffect(() => {
    if (!searchQueryInitialized.current) { searchQueryInitialized.current = true; return; }
    history.push(`/adminpanel/recipes${searchQuery}`);
  }, [searchQuery]);

  useEffect(() => {
    if (!locationSearchInitialized.current) { locationSearchInitialized.current = true; return; }
    const parsed = queryString.parse(location.search) as any;
    let page: any, filterVal: any, query: any;

    if (parsed.page) page = parsed.page;
    if (parsed.filter) filterVal = parsed.filter;
    if (parsed.query) query = parsed.query;

    updateSearchQuery(parsed);
    window.scrollTo(0, 0);
    props.getAdminRecipes(page, filterVal, query);
  }, [location.search]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const parsed = queryString.parse(location.search) as any;
    parsed.query = queryInput;
    parsed.page = undefined;
    setQueryInput('');
    updateSearchQuery(parsed);
  };

  const handleReset = () => setSearchQuery('');

  const previousPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parseInt(parsed.page as string) - 1;
    updateSearchQuery(parsed);
  };

  const nextPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parsed.page ? parseInt(parsed.page as string) + 1 : 2;
    updateSearchQuery(parsed);
  };

  const handleRecipeClick = (recipe: any) => {
    history.push({
      pathname: `/adminpanel/recipes/${recipe.title.replace(/[^A-Z0-9]+/gi, '-')}/${recipe._id}`,
      state: { recipe },
    });
  };

  const parsed = queryString.parse(location.search) as any;
  const PAGE_SIZE = 12;
  const totalPages = Math.ceil(props.adminRecipes.recipeCount / PAGE_SIZE);
  const currentPage = parsed.page
    ? parsed.page > totalPages ? totalPages : parsed.page <= 1 ? 1 : parseInt(parsed.page)
    : 1;
  const end = Math.min(PAGE_SIZE * currentPage, props.adminRecipes.recipeCount);
  const startItem = currentPage <= 1 ? 1 : (currentPage - 1) * PAGE_SIZE + 1;

  return (
    <div>
      <Helmet>
        <title>Recipes - Frozberries Victoria</title>
        <meta name="description" content="Recipes - Frozberries Victoria" />
      </Helmet>

      {props.adminRecipes.loading ? (
        <div className="admin-empty-state">
          <i className="fas fa-spinner fa-spin" />
          <p>Loading recipes…</p>
        </div>
      ) : (
        <div className="admin-card">
          <div className="admin-card-header">
            <p className="admin-card-title">Recipes</p>
            {props.adminRecipes.recipeCount > 0 && (
              <span style={{ fontSize: '0.78rem', color: '#6d7175' }}>
                {startItem}–{end} of {props.adminRecipes.recipeCount}
              </span>
            )}
          </div>

          <SearchBar
            queryInput={queryInput}
            parsedQuery={parsed.query}
            onInputChange={(e) => setQueryInput(e.target.value)}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onNewRecipe={() => history.push('/adminpanel/recipes/new-recipe')}
          />

          {props.adminRecipes.recipeCount === 0 ? (
            <div className="admin-empty-state">
              <i className="fas fa-utensils" />
              <p>No recipes found.</p>
            </div>
          ) : (
            <>
              <div className="admin-card-body-flush">
                <RecipesTable recipes={props.adminRecipes.recipes} onRowClick={handleRecipeClick} />
              </div>

              {totalPages > 1 && props.adminRecipes.recipes.length !== 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrevious={previousPage}
                  onNext={nextPage}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  adminRecipes: state.adminRecipes,
});

export default connect(mapStateToProps, { getAdminRecipes })(RecipesDashBoard);
